import { FormEvent, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost, ApiError } from '../../api/client';
import { Tenant, TenantKind } from '../../api/types';
import { errorMessage, ptBR } from '../../i18n/pt-BR';

interface TenantsResponse {
  tenants: Tenant[];
}
interface TenantResponse {
  tenant: Tenant;
}

const kindOptions: TenantKind[] = ['restaurant', 'bakery', 'other'];

export default function TenantsPage() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'tenants'],
    queryFn: () => apiGet<TenantsResponse>('/admin/tenants'),
  });

  const [name, setName] = useState('');
  const [kind, setKind] = useState<TenantKind>('restaurant');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');

  const createMutation = useMutation({
    mutationFn: () => apiPost<TenantResponse>('/admin/tenants', { name, kind }),
    onSuccess: () => {
      setName('');
      setKind('restaurant');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
    onError: (err) => setError(errorMessage(err instanceof ApiError ? err.code : undefined)),
  });

  const updateMutation = useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      apiPatch<TenantResponse>(`/admin/tenants/${input.id}`, { name: input.name }),
    onSuccess: () => {
      setEditingId(null);
      void queryClient.invalidateQueries({ queryKey: ['admin', 'tenants'] });
    },
    onError: (err) => setError(errorMessage(err instanceof ApiError ? err.code : undefined)),
  });

  function handleCreate(e: FormEvent): void {
    e.preventDefault();
    setError(null);
    createMutation.mutate();
  }

  return (
    <section>
      <h2>{ptBR.admin.tenants.title}</h2>

      {isLoading && <p>{ptBR.common.loading}</p>}

      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: '1px solid #ddd' }}>
            <th>{ptBR.admin.tenants.name}</th>
            <th>{ptBR.admin.tenants.kind}</th>
            <th>{ptBR.common.actions}</th>
          </tr>
        </thead>
        <tbody>
          {data?.tenants.map((tenant) => (
            <tr key={tenant.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td>
                {editingId === tenant.id ? (
                  <input value={editName} onChange={(e) => setEditName(e.target.value)} />
                ) : (
                  tenant.name
                )}
              </td>
              <td>{ptBR.admin.tenants.kindOptions[tenant.kind]}</td>
              <td>
                {editingId === tenant.id ? (
                  <>
                    <button
                      onClick={() => updateMutation.mutate({ id: tenant.id, name: editName })}
                    >
                      {ptBR.common.save}
                    </button>{' '}
                    <button onClick={() => setEditingId(null)}>{ptBR.common.cancel}</button>
                  </>
                ) : (
                  <button
                    onClick={() => {
                      setEditingId(tenant.id);
                      setEditName(tenant.name);
                    }}
                  >
                    {ptBR.common.edit}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h3>{ptBR.admin.tenants.createTitle}</h3>
      <form onSubmit={handleCreate} style={{ display: 'flex', gap: 8, alignItems: 'end' }}>
        <label>
          {ptBR.admin.tenants.name}
          <br />
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>
        <label>
          {ptBR.admin.tenants.kind}
          <br />
          <select value={kind} onChange={(e) => setKind(e.target.value as TenantKind)}>
            {kindOptions.map((k) => (
              <option key={k} value={k}>
                {ptBR.admin.tenants.kindOptions[k]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" disabled={createMutation.isPending}>
          {ptBR.common.create}
        </button>
      </form>
      {error && <p style={{ color: '#c5221f' }}>{error}</p>}
    </section>
  );
}
